import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { 
  Search, 
  Plus, 
  BookOpen, 
  Filter, 
  Tag, 
  Calendar,
  User,
  FileText,
  Trash2,
  Edit,
  ExternalLink,
  Brain,
  TrendingUp,
  Library,
  Upload,
  Download,
  X,
  File,
  Image,
  FileIcon,
  LayoutGrid,
  List,
  Eye,
  Paperclip,
  Maximize2,
  MoreVertical,
  ChevronDown
} from 'lucide-react';
import TagService from '../../components/Tags/TagService';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useParams, useLocation } from 'react-router-dom';

const KnowledgeCenter = () => {
  const { t } = useTranslation();
  const { user, hasRole, getCurrentTenant } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const { articleId: routeArticleId } = useParams();
  const location = useLocation();
  const directArticleId = useMemo(() => {
    const parseId = (value) => {
      if (!value) return null;
      const parsed = parseInt(value, 10);
      if (Number.isNaN(parsed) || parsed <= 0) return null;
      return parsed;
    };

    const paramId = parseId(routeArticleId);
    if (paramId) return paramId;

    const searchParams = new URLSearchParams(location.search);
    const queryId = searchParams.get('articleId');
    return parseId(queryId);
  }, [routeArticleId, location.search]);
  const processedDeepLinkIdRef = useRef(null);
  
  console.log('🔍 KnowledgeCenter: Component mounted/rendered', {
    user: !!user,
    userId: user?.userId,
    hasRole: typeof hasRole,
    getCurrentTenant: typeof getCurrentTenant,
    getProtectedTenantCode: typeof getProtectedTenantCode
  });
  const [articles, setArticles] = useState([]);
  const buildApiUrl = useCallback((path) => {
    const baseUrl = getApiBaseUrl();
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (!baseUrl) {
      return normalizedPath;
    }
    return `${baseUrl}${normalizedPath}`;
  }, []);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSourceType, setSelectedSourceType] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [viewingArticle, setViewingArticle] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);

  // Enhanced attachment viewer state
  const [showViewAttachmentModal, setShowViewAttachmentModal] = useState(false);
  const [selectedAttachmentForView, setSelectedAttachmentForView] = useState(null);
  const [viewingAttachment, setViewingAttachment] = useState(false);
  const [attachmentViewUrl, setAttachmentViewUrl] = useState(null);
  const [modalSize, setModalSize] = useState({ width: '90%', height: '85%' });
  const [isResizing, setIsResizing] = useState(false);

  // Check if user is manager/admin (can manage all articles)
  const isManager = hasRole(['Manager', 'Admin']);
  // All authenticated users can create articles
  const canCreate = !!user;
  
  // Check tenant context with fallback - use same approach as other working pages
  const currentTenant = getCurrentTenant();
  let tenantCode = currentTenant?.code || currentTenant?.tenant_code || user?.selectedTenant?.code || user?.defaultTenant?.code || user?.tenantCode || 'DEMO';
  
  // If no tenant code or it's DEFAULT, this indicates a problem - force refresh like ChatLogExplorer does
  if (!tenantCode || tenantCode === 'DEFAULT') {
    console.warn('🚨 KnowledgeCenter: Tenant context lost! Attempting to refresh user session...');
    // Use DEMO as fallback instead of forcing refresh to avoid infinite loop
    tenantCode = 'DEMO';
  }
  
  // Debug tenant context
  useEffect(() => {
    if (user) {
      console.log('🔍 KnowledgeCenter Debug:', {
        userId: user.userId,
        userType: user.userType,
        selectedTenant: user.selectedTenant,
        defaultTenant: user.defaultTenant,
        tenants: user.tenants,
        currentTenant: currentTenant,
        finalTenantCode: tenantCode,
        getCurrentTenant: getCurrentTenant()
      });
    }
  }, [user, tenantCode, currentTenant, getCurrentTenant]);
  
  // Helper function to check if user can edit/delete a specific article
  const canModifyArticle = (article) => {
    if (!user) return false;
    // Manager/Admin can modify any article
    if (isManager) return true;
    // User can modify their own articles
    return article.CreatedBy === user.userId;
  };

  // Helper function to properly decode UTF-8 filenames
  const decodeFileName = (filename) => {
    if (!filename) return '';
    
    try {
      let decoded = filename;
      
      // Handle URL encoding (most common case)
      if (filename.includes('%')) {
        try {
          decoded = decodeURIComponent(filename);
        } catch (urlError) {
          console.warn('URL decode failed, trying alternative methods:', urlError);
        }
      }
      
      // Handle potential double encoding
      if (decoded.includes('%') && decoded !== filename) {
        try {
          decoded = decodeURIComponent(decoded);
        } catch (doubleUrlError) {
          console.warn('Double URL decode failed:', doubleUrlError);
        }
      }
      
      // Handle HTML entities
      if (decoded.includes('&')) {
        try {
          const textarea = document.createElement('textarea');
          textarea.innerHTML = decoded;
          decoded = textarea.value;
        } catch (htmlError) {
          console.warn('HTML entity decode failed:', htmlError);
        }
      }
      
      // Handle potential Unicode escape sequences
      if (decoded.includes('\\u') || decoded.includes('\\x')) {
        try {
          decoded = decoded.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
            return String.fromCharCode(parseInt(hex, 16));
          });
          decoded = decoded.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
            return String.fromCharCode(parseInt(hex, 16));
          });
        } catch (unicodeError) {
          console.warn('Unicode decode failed:', unicodeError);
        }
      }
      
      // Fix UTF-8 encoding issues (like à¸ characters)
      if (decoded.includes('à¸') || decoded.includes('à¹') || decoded.includes('à¸£') || decoded.includes('à¸´')) {
        try {
          const latin1Buffer = new Uint8Array(decoded.length);
          for (let i = 0; i < decoded.length; i++) {
            latin1Buffer[i] = decoded.charCodeAt(i);
          }
          const utf8Decoder = new TextDecoder('utf-8');
          const corrected = utf8Decoder.decode(latin1Buffer);
          if (corrected !== decoded && corrected.length > 0) {
            decoded = corrected;
          }
        } catch (utf8Error) {
          console.warn('UTF-8 encoding correction failed:', utf8Error);
        }
      }
      
      return decoded !== filename && decoded.length > 0 && decoded.length < 255 ? decoded : filename;
      
    } catch (error) {
      console.error('Failed to decode filename:', filename, error);
      return filename;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isViewableFileType = (mimeType) => {
    if (!mimeType) return false;
    
    const viewableTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
      // Documents
      'application/pdf',
      // Text files
      'text/plain', 'text/html', 'text/css', 'text/javascript', 'text/xml',
      'application/json', 'application/xml',
      // Code files
      'text/csv', 'text/markdown', 'text/yaml', 'text/yml',
      // Audio/Video (some browsers can play these)
      'audio/mpeg', 'audio/wav', 'audio/ogg',
      'video/mp4', 'video/webm', 'video/ogg'
    ];
    
    return viewableTypes.includes(mimeType.toLowerCase());
  };

  const handleViewAttachment = async (attachment) => {
    console.log('🔍 Starting to view Knowledge Center attachment:', {
      attachmentId: attachment.FileID,
      fileName: attachment.FileName,
      mimeType: attachment.MimeType,
      fileSize: attachment.FileSize
    });

    setViewingAttachment(true);
    setSelectedAttachmentForView(attachment);

    try {
      console.log('🔍 handleViewAttachment: Using tenant code:', tenantCode);
      
      // Use Knowledge Center specific endpoint
      const response = await fetch(buildApiUrl(`/api/knowledge/files/${attachment.FileID}/view`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      console.log('🔍 Response status:', response.status);

      if (response.ok) {
        const blob = await response.blob();
        console.log('🔍 Blob received:', {
          size: blob.size,
          type: blob.type
        });
        
        // Check if the blob is valid
        if (blob.size === 0) {
          throw new Error('Attachment file is empty or corrupted');
        }
        
        const url = window.URL.createObjectURL(blob);
        console.log('🔍 Created blob URL:', url);
        setAttachmentViewUrl(url);
        setShowViewAttachmentModal(true);
      } else {
        const errorText = await response.text();
        console.error('🔍 Server error response:', errorText);
        throw new Error(`Failed to load attachment: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('🔍 Error viewing attachment:', error);
      toast.error(`Failed to view attachment: ${error.message}`);
      setShowViewAttachmentModal(false);
    } finally {
      setViewingAttachment(false);
    }
  };

  const handleCloseViewAttachment = () => {
    setShowViewAttachmentModal(false);
    setSelectedAttachmentForView(null);
    if (attachmentViewUrl) {
      window.URL.revokeObjectURL(attachmentViewUrl);
      setAttachmentViewUrl(null);
    }
    // Reset modal size when closing
    setModalSize({ width: '90%', height: '85%' });
  };

  // Handle modal resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const container = document.querySelector('.modal-container');
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newWidth = Math.max(600, Math.min(window.innerWidth - 100, e.clientX - rect.left));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 100, e.clientY - rect.top));
      
      setModalSize({
        width: `${newWidth}px`,
        height: `${newHeight}px`
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Handle Escape key to close view attachment modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showViewAttachmentModal) {
        handleCloseViewAttachment();
      }
    };

    if (showViewAttachmentModal) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showViewAttachmentModal]);

  const handleDownloadAttachment = async (attachment) => {
    try {
      console.log('🔍 handleDownloadAttachment: Using tenant code:', tenantCode);
      
      // Use Knowledge Center specific endpoint
      const response = await fetch(buildApiUrl(`/api/knowledge/files/${attachment.FileID}/download`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = decodeFileName(attachment.FileName);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Failed to download file');
      }
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  useEffect(() => {
    console.log('🔍 useEffect: Tenant code check:', {
      tenantCode,
      hasTenantCode: !!tenantCode,
      currentTenant,
      user,
      loading
    });
    
    if (tenantCode && tenantCode !== 'DEFAULT') {
      const debounceMs = searchQuery ? 350 : 120;
      const timeoutId = setTimeout(() => {
        console.log('🔍 useEffect: Calling loadData() with tenant:', tenantCode);
        loadData();
      }, debounceMs);
      return () => clearTimeout(timeoutId);
    }
    console.log('🔍 useEffect: No valid tenant code, not loading data. Tenant code:', tenantCode);
  }, [searchQuery, selectedCategory, selectedSourceType, selectedTag, currentPage, viewMode, tenantCode]);

  // Auto-show filter bar when filters are active
  useEffect(() => {
    const hasActiveFilters = searchQuery || selectedCategory || selectedSourceType || selectedTag;
    if (hasActiveFilters) {
      setShowFilters(true);
    }
  }, [searchQuery, selectedCategory, selectedSourceType, selectedTag]);

  // Tags are loaded on demand when user opens the tag dropdown (handleTagDropdownFocus)
  const fetchAvailableTags = async () => {
    try {
      setTagsLoading(true);
      const response = await fetch('/api/tags?entityType=Knowledge', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data.tags || []);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      // Don't show error to user, tags are optional
    } finally {
      setTagsLoading(false);
    }
  };

  const handleTagDropdownFocus = () => {
    if (availableTags.length === 0 && !tagsLoading) {
      fetchAvailableTags();
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔍 KnowledgeCenter: Starting to load data...');
      console.log('🔍 KnowledgeCenter: Tenant code:', getProtectedTenantCode());
      console.log('🔍 KnowledgeCenter: User:', user);
      
      const [articlesRes, categoriesRes, statsRes] = await Promise.all([
        loadArticles(),
        loadCategories(),
        loadStats()
      ]);
      
      console.log('🔍 KnowledgeCenter: Data loaded successfully:', {
        articles: articlesRes?.articles?.length || 0,
        categories: categoriesRes?.length || 0,
        stats: statsRes
      });
    } catch (error) {
      console.error('🔍 KnowledgeCenter: Error loading data:', error);
      toast.error('Failed to load knowledge center data');
    } finally {
      setLoading(false);
    }
  };

  const loadArticles = async () => {
    try {
      console.log('🔍 loadArticles: Using tenant code:', tenantCode);
      
      if (!tenantCode || tenantCode === 'DEFAULT') {
        console.warn('🔍 loadArticles: No valid tenant code available for loading articles. Tenant code:', tenantCode);
        setArticles([]);
        setTotalPages(1);
        return { articles: [], pagination: { pages: 1 } };
      }

      const limit = viewMode === 'card' ? 12 : 20; // Card view: 12, List view: 20
      const params = new URLSearchParams({
        page: currentPage,
        limit: limit,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedSourceType && { sourceType: selectedSourceType }),
        ...(selectedTag && { tag: selectedTag })
      });

      console.log('🔍 loadArticles: Making API call to:', `/knowledge/articles?${params}`);
      const response = await api.get(`/knowledge/articles?${params}`);
      console.log('🔍 loadArticles: API response:', response);
      
      setArticles(response.articles || []);
      setTotalPages(response.pagination?.pages || 1);
      return response;
    } catch (error) {
      console.error('🔍 loadArticles: Error loading articles:', error);
      toast.error('Failed to load articles');
      setArticles([]);
      setTotalPages(1);
      return { articles: [], pagination: { pages: 1 } };
    }
  };

  const loadCategories = async () => {
    try {
      if (!tenantCode || tenantCode === 'DEFAULT') {
        console.warn('🔍 loadCategories: No valid tenant code available for loading categories. Tenant code:', tenantCode);
        setCategories([]);
        return [];
      }

      const response = await api.get('/knowledge/categories');
      setCategories(Array.isArray(response) ? response : []);
      return response;
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
      return [];
    }
  };

  const loadStats = async () => {
    try {
      if (!tenantCode || tenantCode === 'DEFAULT') {
        console.warn('🔍 loadStats: No valid tenant code available for loading stats. Tenant code:', tenantCode);
        setStats({});
        return {};
      }

      const response = await api.get('/knowledge/stats');
      setStats(response || {});
      return response;
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({});
      return {};
    }
  };



  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      console.log('🔍 handleDeleteArticle: Using tenant code:', tenantCode);
      
      if (!tenantCode || tenantCode === 'DEFAULT') {
        toast.error('Unable to delete article: Invalid tenant context');
        return;
      }

      await api.delete(`/knowledge/articles/${articleId}`);
      toast.success('Article deleted successfully');
      loadArticles();
    } catch (error) {
      console.error('🔍 Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedSourceType('');
    setSelectedTag('');
    setCurrentPage(1);
  };

  const handleViewArticle = useCallback(async (article) => {
    try {
      setLoadingArticle(true);
      console.log('🔍 handleViewArticle: Using tenant code:', tenantCode);
      
      if (!tenantCode || tenantCode === 'DEFAULT') {
        toast.error('Unable to load article: Invalid tenant context');
        return;
      }

      const response = await api.get(`/knowledge/articles/${article.ArticleID}`);
      const fullArticle = response.article || response;

      if (!fullArticle || !fullArticle.ArticleID) {
        throw new Error('Invalid article response');
      }

      setViewingArticle(fullArticle);
      setArticles(prev => {
        if (!Array.isArray(prev)) {
          return prev;
        }

        const existingIndex = prev.findIndex(item => item.ArticleID === fullArticle.ArticleID);
        if (existingIndex === -1) {
          return [fullArticle, ...prev];
        }

        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...fullArticle };
        return updated;
      });
    } catch (error) {
      console.error('🔍 Error loading full article:', error);
      toast.error('Failed to load article content');
      setViewingArticle(article);
    } finally {
      setLoadingArticle(false);
    }
  }, [tenantCode]);

  useEffect(() => {
    if (!directArticleId) {
      processedDeepLinkIdRef.current = null;
    }
  }, [directArticleId]);

  useEffect(() => {
    if (!tenantCode || !user) return;
    if (!directArticleId) return;
    if (processedDeepLinkIdRef.current === directArticleId) return;

    processedDeepLinkIdRef.current = directArticleId;
    handleViewArticle({ ArticleID: directArticleId });
  }, [tenantCode, user, directArticleId, handleViewArticle]);

    const handleEditArticle = async (article) => {
    try {
      setLoadingEdit(true);
      
      // Validate article and ArticleID
      if (!article) {
        toast.error('Invalid article: Article data is missing');
        return;
      }
      
      if (!article.ArticleID) {
        console.warn('🔍 handleEditArticle: ArticleID is missing, using preview data directly:', article);
        // If ArticleID is missing, use the article data directly (might be a new article or incomplete data)
        setEditingArticle(article);
        return;
      }
      
      console.log('🔍 handleEditArticle: Starting to fetch full article for:', article.ArticleID);
      console.log('🔍 handleEditArticle: Using tenant code:', tenantCode);
      
      if (!tenantCode || tenantCode === 'DEFAULT') {
        toast.error('Unable to edit article: Invalid tenant context');
        return;
      }
  
      // Fetch full article content from API for editing
      const response = await api.get(`/knowledge/articles/${article.ArticleID}`);
      console.log('handleEditArticle: API response:', response);
      
      const fullArticle = response.article || response;
      
      // Validate the response
      if (!fullArticle || !fullArticle.ArticleID) {
        throw new Error('Invalid article response from server');
      }
      
      console.log('handleEditArticle: Setting editing article to:', fullArticle);
      
      setEditingArticle(fullArticle);
    } catch (error) {
      console.error('Error loading full article for editing:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 404) {
        toast.error('Article not found. It may have been deleted.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to edit this article');
      } else if (error.message) {
        toast.error(`Failed to load article: ${error.message}`);
      } else {
        toast.error('Failed to load article content for editing');
      }
      
      // Fallback to using the preview data only if we have valid article data
      if (article && article.ArticleID) {
        console.warn('🔍 handleEditArticle: Falling back to preview data:', article);
        setEditingArticle(article);
      } else {
        // If we don't have valid article data, don't open the edit modal
        setEditingArticle(null);
      }
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleProcessPDFsForRAG = async (article) => {
    try {
      console.log('🔍 handleProcessPDFsForRAG: Using tenant code:', tenantCode);
      
      if (!tenantCode || tenantCode === 'DEFAULT') {
        toast.error('Unable to process PDFs: Invalid tenant context');
        return;
      }

      toast.loading('Processing PDFs for RAG ingestion...', { id: 'pdf-processing' });
      
      const response = await api.post(`/knowledge/articles/${article.ArticleID}/process-pdfs`);
      
      toast.success('PDFs processed successfully for RAG!', { id: 'pdf-processing' });
      
      // Show processing results
      const { data } = response;
      if (data.successfulExtractions > 0) {
        toast.success(`${data.successfulExtractions} PDF(s) processed successfully. Content is now available for RAG ingestion.`);
      }
      
      if (data.failedExtractions > 0) {
        toast.error(`${data.failedExtractions} PDF(s) failed to process. Check logs for details.`);
      }
      
    } catch (error) {
      console.error('Error processing PDFs for RAG:', error);
      toast.error('Failed to process PDFs for RAG ingestion', { id: 'pdf-processing' });
      
      if (error.response?.data?.error) {
        toast.error(`Error: ${error.response.data.error}`);
      }
    }
  };

  const handleBulkProcessPDFsForRAG = async () => {
    try {
      console.log('🔍 handleBulkProcessPDFsForRAG: Using tenant code:', tenantCode);
      
      if (!tenantCode || tenantCode === 'DEFAULT') {
        toast.error('Unable to process PDFs: Invalid tenant context');
        return;
      }

      // Find all articles with PDF attachments
      const articlesWithPDFs = articles.filter(article => 
        article.files && article.files.some(file => file.MimeType === 'application/pdf')
      );

      if (articlesWithPDFs.length === 0) {
        toast('No articles with PDF attachments found.');
        return;
      }

      if (!window.confirm(`Process PDFs for ${articlesWithPDFs.length} articles? This will extract text content for RAG ingestion.`)) {
        return;
      }

      toast.loading(`Processing PDFs for ${articlesWithPDFs.length} articles...`, { id: 'bulk-pdf-processing' });
      
      let totalProcessed = 0;
      let totalSuccessful = 0;
      let totalFailed = 0;

      for (const article of articlesWithPDFs) {
        try {
          const response = await api.post(`/knowledge/articles/${article.ArticleID}/process-pdfs`);
          const { data } = response;
          
          totalProcessed++;
          totalSuccessful += data.successfulExtractions || 0;
          totalFailed += data.failedExtractions || 0;
          
          // Update progress
          toast.loading(`Processed ${totalProcessed}/${articlesWithPDFs.length} articles...`, { id: 'bulk-pdf-processing' });
          
        } catch (error) {
          totalProcessed++;
          totalFailed++;
          console.error(`Error processing PDFs for article ${article.ArticleID}:`, error);
        }
      }

      toast.success(`Bulk PDF processing completed!`, { id: 'bulk-pdf-processing' });
      toast.success(`${totalSuccessful} PDF(s) processed successfully. ${totalFailed} failed.`);
      
    } catch (error) {
      console.error('Error in bulk PDF processing:', error);
      toast.error('Failed to complete bulk PDF processing', { id: 'bulk-pdf-processing' });
      
      if (error.response?.data?.error) {
        toast.error(`Error: ${error.response.data.error}`);
      }
    }
  };

  const getSourceTypeIcon = (sourceType) => {
    switch (sourceType) {
      case 'Manual': return <Edit className="h-4 w-4" />;
      case 'Excel': return <FileText className="h-4 w-4" />;
      case 'PDF': return <FileText className="h-4 w-4" />;
      case 'AI': return <Brain className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getSourceTypeColor = (sourceType) => {
    switch (sourceType) {
      case 'Manual': return 'bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200';
      case 'Excel': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'PDF': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'AI': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  // Show tenant validation error if no valid tenant context (but allow DEMO as fallback)
  if (!tenantCode || tenantCode === 'INVALID') {
    console.log('🔍 KnowledgeCenter: Showing invalid tenant message. Tenant code:', tenantCode);
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 text-center">
          <Library className="h-12 w-12 text-yellow-600 dark:text-yellow-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
            {t('knowledgeCenter.invalidTenant', 'Invalid Tenant Context')}
          </h3>
          <p className="text-yellow-700 dark:text-yellow-300">
            {t('knowledgeCenter.invalidTenantDesc', 'Please select a valid tenant to access the Knowledge Center.')}
          </p>
          <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-2">
            Debug: Tenant code is {tenantCode || 'null'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header - Title and Actions in Same Line */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        {/* Title Section */}
        <div className="flex-shrink-0">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Library className="h-8 w-8 text-teal-600 dark:text-teal-400" />
            {t('knowledgeCenter.title', 'Knowledge Center')}
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">{t('knowledgeCenter.description', 'Central knowledge base for frequently asked questions, product catalogs, troubleshooting guides, and user manuals.')}</p>
          <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700">
            💡 <strong>{t('knowledgeCenter.permissionInfo', 'Permission Info')}:</strong> {t('knowledgeCenter.permissionDesc', 'You can create new articles and edit/delete your own articles')}. 
            {isManager && <span> {t('knowledgeCenter.managerPermission', 'As a manager, you can edit/delete any article')}.</span>}
          </div>
        </div>
        
        {/* Action Buttons - Collapsible Menu */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
          {/* View Mode Toggle - Always Visible */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => {
                setViewMode('card');
                setCurrentPage(1);
              }}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'card'
                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Card View"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">Card</span>
            </button>
            <button
              onClick={() => {
                setViewMode('list');
                setCurrentPage(1);
              }}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">List</span>
            </button>
          </div>
          
          {/* Filter Button - Between View Toggle and Action Buttons */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
              searchQuery || selectedCategory || selectedSourceType || selectedTag
                ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                : showFilters
                ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
            }`}
            title="Toggle filters"
          >
            <Filter className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{t('common.filter', 'Filter')}</span>
            {(searchQuery || selectedCategory || selectedSourceType || selectedTag) && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-bold">
                {(searchQuery ? 1 : 0) + (selectedCategory ? 1 : 0) + (selectedSourceType ? 1 : 0) + (selectedTag ? 1 : 0)}
              </span>
            )}
          </button>
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Expandable Menu Buttons - Always visible on mobile, collapsible on desktop */}
            <div className={`flex flex-wrap items-center gap-2 ${menuExpanded ? 'sm:flex' : 'sm:hidden'}`}>
              {/* No expandable buttons currently - can add if needed */}
            </div>
            
            {/* Expand/Collapse Button - Only on Desktop (hidden if no expandable buttons) */}
            
            {/* Create Article Button - Always Visible */}
            {canCreate && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
              >
                <Plus className="h-5 w-5 flex-shrink-0" />
                <span>{t('knowledgeCenter.createArticle', 'Create Article')}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('knowledgeCenter.totalArticles', 'Total Articles')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.TotalArticles || 0}</p>
            </div>
            <BookOpen className="h-8 w-8 text-teal-600 dark:text-teal-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('knowledgeCenter.categories', 'Categories')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.TotalCategories || 0}</p>
            </div>
            <Tag className="h-8 w-8 text-green-600 dark:text-green-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('knowledgeCenter.recentArticles', 'Recent Articles')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.RecentArticles || 0}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-purple-600 dark:text-purple-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{t('knowledgeCenter.aiGenerated', 'AI Generated')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.AIArticles || 0}</p>
            </div>
            <Brain className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
          </div>
        </div>
      </div>

      {/* Search and Filters - Hidden by Default */}
      {showFilters && (
        <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
          searchQuery || selectedCategory || selectedSourceType || selectedTag
            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }`}>
          {(searchQuery || selectedCategory || selectedSourceType || selectedTag) && (
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                🔍 Filters Active
              </span>
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
              >
                Clear All
              </button>
            </div>
          )}
          <div className="space-y-4">
            {/* Regular Search */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder={t('knowledgeCenter.searchPlaceholder', 'Search articles by title, content, or tags...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  />
                </div>
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('knowledgeCenter.allCategories', 'All Categories')}</option>
                {(categories || []).map((cat) => (
                  <option key={cat.Category} value={cat.Category}>
                    {cat.Category} ({cat.ArticleCount})
                  </option>
                ))}
              </select>
              <select
                value={selectedSourceType}
                onChange={(e) => setSelectedSourceType(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{t('knowledgeCenter.allSources', 'All Sources')}</option>
                <option value="Manual">Manual</option>
                <option value="Excel">Excel</option>
                <option value="PDF">PDF</option>
                <option value="AI">AI Generated</option>
              </select>
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                onFocus={handleTagDropdownFocus}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                title="Filter by tag"
              >
                <option value="">{t('knowledgeCenter.allTags', 'All Tags')}</option>
                {availableTags && availableTags.length > 0 ? (
                  availableTags.map((tag) => (
                    <option key={tag.id || tag.name} value={tag.name}>
                      {tag.name} ({tag.usageCount || 0})
                    </option>
                  ))
                ) : tagsLoading ? (
                  <option value="" disabled>Loading tags...</option>
                ) : null}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="space-y-6">
        {/* Articles Display */}
        {loading ? (
          viewMode === 'card' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-2/3 mb-4"></div>
                  <div className="flex justify-between">
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/4"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3 mb-2 animate-pulse"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/3 animate-pulse"></div>
                  </div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-20 animate-pulse"></div>
                </div>
              ))}
            </div>
          )
        ) : (
          <>
            {viewMode === 'card' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(articles || []).map((article) => (
                <div key={article.ArticleID} className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border transition-shadow hover:shadow-md ${
                  article.CreatedBy === user?.userId 
                    ? 'border-teal-200 dark:border-teal-700 bg-teal-50/30 dark:bg-teal-900/20' 
                    : 'border-gray-200 dark:border-gray-700'
                }`}
                onDoubleClick={() => handleViewArticle(article)}
                role="button"
                tabIndex={0}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    handleViewArticle(article);
                  }
                }}
              >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3
                        className="font-semibold text-gray-900 dark:text-white line-clamp-2 flex-1 cursor-pointer hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                        onClick={() => handleViewArticle(article)}
                      >
                        {article.Title}
                      </h3>
                      {canModifyArticle(article) && (
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => handleEditArticle(article)}
                            disabled={loadingEdit}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isManager ? "Edit article (Manager)" : "Edit your article"}
                          >
                            {loadingEdit ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600 dark:border-teal-400"></div>
                            ) : (
                              <Edit className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article.ArticleID)}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title={isManager ? "Delete article (Manager)" : "Delete your article"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-300 text-sm mb-3 line-clamp-3">{article.ContentPreview}</p>
                    
                    {/* Read More Link at end of content */}
                    <div className="mb-4">
                      <button 
                        onClick={() => handleViewArticle(article)}
                        className="text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 text-sm font-medium flex items-center gap-1 transition-colors"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {t('common.readMore', 'Read More')}
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      {article.Category && (
                        <span className="bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 text-xs px-2 py-1 rounded-full">
                          {article.Category}
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${getSourceTypeColor(article.SourceType)}`}>
                        {getSourceTypeIcon(article.SourceType)}
                        {article.SourceType}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span className={article.CreatedBy === user?.userId ? 'font-medium text-teal-600 dark:text-teal-400' : ''}>
                            {article.CreatedByName}
                            {article.CreatedBy === user?.userId && (
                              <span className="ml-1 text-teal-600 dark:text-teal-400">(You)</span>
                            )}
                          </span>
                        </div>
                        {article.FileCount > 0 && (
                          <div className="flex items-center gap-1 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full">
                            <Paperclip className="h-3 w-3" />
                            <span className="text-xs font-medium">{article.FileCount}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(article.UpdatedAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-3">
                    <div className="flex items-center justify-between">
                      {/* Tags Section at bottom */}
                      {article.Tags && (
                        <div className="flex-1">
                          <TagService
                            entityType="Knowledge"
                            entityId={article.ArticleID}
                            initialTags={article.Tags ? (typeof article.Tags === 'string' ? article.Tags.split(',').map(t => t.trim()).filter(Boolean) : article.Tags) : []}
                            readonly={true}
                            maxTags={15}
                            onChange={() => {}}
                          />
                        </div>
                      )}
                      
                      {/* PDF Processing for RAG Button */}
                      {article.files && article.files.some(file => file.MimeType === 'application/pdf') && (
                        <button
                          onClick={() => handleProcessPDFsForRAG(article)}
                          className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 text-sm font-medium flex items-center gap-1 transition-colors ml-3"
                          title="Process PDFs for RAG ingestion"
                        >
                          <Brain className="h-4 w-4" />
                          Process PDFs
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            ) : (
              /* List View */
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(articles || []).map((article) => (
                    <div 
                      key={article.ArticleID} 
                      className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer ${
                        article.CreatedBy === user?.userId ? 'bg-teal-50/30 dark:bg-teal-900/20' : ''
                      }`}
                      onClick={() => handleViewArticle(article)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <h3
                                className="font-medium text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors line-clamp-1 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewArticle(article);
                                }}
                              >
                                {article.Title}
                              </h3>
                              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  <span className={article.CreatedBy === user?.userId ? 'font-medium text-teal-600 dark:text-teal-400' : ''}>
                                    {article.CreatedByName}
                                    {article.CreatedBy === user?.userId && (
                                      <span className="ml-1 text-teal-600 dark:text-teal-400">(You)</span>
                                    )}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  <span>{new Date(article.UpdatedAt).toLocaleDateString()}</span>
                                </div>
                                {article.Category && (
                                  <span className="bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 text-xs px-2 py-1 rounded-full">
                                    {article.Category}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {article.FileCount > 0 && (
                                <div className="flex items-center gap-1 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-full">
                                  <Paperclip className="h-3 w-3" />
                                  <span className="text-xs font-medium">{article.FileCount}</span>
                                </div>
                              )}
                              <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${getSourceTypeColor(article.SourceType)}`}>
                                {getSourceTypeIcon(article.SourceType)}
                                {article.SourceType}
                              </span>
                              {canModifyArticle(article) && (
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditArticle(article);
                                    }}
                                    disabled={loadingEdit}
                                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title={isManager ? "Edit article (Manager)" : "Edit your article"}
                                  >
                                    {loadingEdit ? (
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-600 dark:border-teal-400"></div>
                                    ) : (
                                      <Edit className="h-3 w-3" />
                                    )}
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteArticle(article.ArticleID);
                                    }}
                                    className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                    title={isManager ? "Delete article (Manager)" : "Delete your article"}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                  
                                  {/* PDF Processing for RAG Button */}
                                  {article.files && article.files.some(file => file.MimeType === 'application/pdf') && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProcessPDFsForRAG(article);
                                      }}
                                      className="p-1 text-green-400 dark:text-green-500 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                                      title="Process PDFs for RAG ingestion"
                                    >
                                      <Brain className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              )}
                              <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.previous')}
                </button>
                
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900 border border-teal-300 dark:border-teal-700'
                          : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}

        {!loading && (articles || []).length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('knowledgeCenter.noArticles', 'No Articles Found')}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{t('knowledgeCenter.noArticlesDesc', 'No articles match your search criteria. Try adjusting your filters or create a new article.')}</p>
            {canCreate && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t('knowledgeCenter.createFirstArticle')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <ArticleModal 
          onClose={() => setShowCreateModal(false)} 
          onSave={loadArticles} 
          setModalSize={setModalSize}
        />
      )}
      {editingArticle && (
        <ArticleModal 
          article={editingArticle} 
          onClose={() => setEditingArticle(null)} 
          onSave={loadArticles} 
        />
      )}
             {viewingArticle && (
         <ArticleViewModal 
           article={viewingArticle} 
           onClose={() => setViewingArticle(null)}
           onEdit={(article) => {
             setViewingArticle(null);
             handleEditArticle(article);
           }}
           onDelete={handleDeleteArticle}
           loading={loadingArticle}
           canModify={canModifyArticle(viewingArticle)}
           isManager={isManager}
           user={user}
           onRefresh={loadArticles}
           // Attachment viewer props
           handleViewAttachment={handleViewAttachment}
           handleDownloadAttachment={handleDownloadAttachment}
           handleCloseViewAttachment={handleCloseViewAttachment}
           handleMouseDown={handleMouseDown}
           decodeFileName={decodeFileName}
           formatFileSize={formatFileSize}
           viewingAttachment={viewingAttachment}
           showViewAttachmentModal={showViewAttachmentModal}
          selectedAttachmentForView={selectedAttachmentForView}
          attachmentViewUrl={attachmentViewUrl}
          modalSize={modalSize}
          setModalSize={setModalSize}
          buildApiUrl={buildApiUrl}
        />
       )}
    </div>
  );
};

// Article Modal Component with file management
const ArticleModal = ({ article, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: article?.Title || '',
    content: article?.Content || '',
    category: article?.Category || '',
    tags: article?.Tags || '',
    sourceType: article?.SourceType || 'Manual'
  });
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);

  // Update form data when article prop changes (for editing)
  useEffect(() => {
    console.log('ArticleModal: article prop changed:', article);
    if (article) {
      console.log('ArticleModal: Setting form data with:', {
        title: article.Title,
        content: article.Content,
        category: article.Category,
        tags: article.Tags,
        sourceType: article.SourceType
      });
      
      setFormData({
        title: article.Title || '',
        content: article.Content || '',
        category: article.Category || '',
        tags: article.Tags || '',
        sourceType: article.SourceType || 'Manual'
      });
      
      // Load existing files when editing an article
      if (article.ArticleID) {
        loadArticleFiles(article.ArticleID);
      }
    } else {
      // Reset form when creating new article
      setFormData({
        title: '',
        content: '',
        category: '',
        tags: '',
        sourceType: 'Manual'
      });
      setFiles([]);
      setSelectedFiles([]);
    }
  }, [article]);

  const loadArticleFiles = async (articleId) => {
    try {
      setLoadingFiles(true);
      const response = await api.get(`/knowledge/articles/${articleId}/files`);
      setFiles(response.files || []);
    } catch (error) {
      console.error('Error loading article files:', error);
      toast.error('Failed to load article files');
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = [];
    const maxFileSize = 50 * 1024 * 1024; // 50MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif'
    ];

    for (const file of newFiles) {
      // Check file size
      if (file.size > maxFileSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 50MB.`);
        continue;
      }

      // Check file type
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File "${file.name}" has an unsupported format.`);
        continue;
      }

      // Check if file already selected
      const isDuplicate = selectedFiles.some(existing => 
        existing.name === file.name && existing.size === file.size
      );
      if (isDuplicate) {
        toast.warning(`File "${file.name}" is already selected.`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} file(s) selected for upload.`);
    }

    // Clear the input
    e.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (articleId) => {
    if (selectedFiles.length === 0) return;

    // Additional validation before upload
    const maxTotalSize = 200 * 1024 * 1024; // 200MB total
    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    
    if (totalSize > maxTotalSize) {
      toast.error('Total file size exceeds 200MB limit. Please remove some files.');
      return;
    }

    if (selectedFiles.length > 10) {
      toast.error('Maximum 10 files allowed per upload. Please remove some files.');
      return;
    }

    try {
      setUploadingFiles(true);
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.uploadFile(`/knowledge/articles/${articleId}/files`, formData);

      toast.success(response.message || `${selectedFiles.length} file(s) uploaded successfully`);
      setSelectedFiles([]);
      await loadArticleFiles(articleId);
    } catch (error) {
      console.error('Error uploading files:', error);
      let errorMessage = 'Failed to upload files';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingFiles(false);
    }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/knowledge/files/${fileId}`);
      toast.success('File deleted successfully');
      setFiles(prev => prev.filter(f => f.FileID !== fileId));
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  const downloadFile = async (fileId, fileName) => {
    try {
      const response = await api.get(`/knowledge/files/${fileId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (mimeType?.includes('pdf')) return <FileText className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let articleId;
      
      // Validate article and ArticleID before attempting update
      if (article) {
        // Check if ArticleID exists and is valid
        if (!article.ArticleID) {
          console.error('ArticleModal: Cannot update article - ArticleID is missing:', article);
          toast.error('Cannot save: Article ID is missing. Please try creating a new article instead.');
          return;
        }
        
        console.log('ArticleModal: Updating article with ID:', article.ArticleID);
        await api.put(`/knowledge/articles/${article.ArticleID}`, formData);
        articleId = article.ArticleID;
        toast.success('Article updated successfully');
      } else {
        // Creating new article
        console.log('ArticleModal: Creating new article');
        const response = await api.post('/knowledge/articles', formData);
        
        // Validate response
        if (!response || !response.article || !response.article.ArticleID) {
          throw new Error('Invalid response from server: Article ID not returned');
        }
        
        articleId = response.article.ArticleID;
        toast.success('Article created successfully');
      }

      // Upload files if any are selected
      if (selectedFiles.length > 0 && articleId) {
        await uploadFiles(articleId);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving article:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 404) {
        toast.error('Article not found. It may have been deleted.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to save this article');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.error || 'Invalid article data. Please check your input.');
      } else if (error.message) {
        toast.error(`Failed to save article: ${error.message}`);
      } else {
        toast.error('Failed to save article');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {article ? t('knowledgeCenter.editArticle') : t('knowledgeCenter.createArticle')}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('knowledgeCenter.articleTitle')}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('knowledgeCenter.content')}
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({...formData, content: e.target.value})}
              rows="10"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('knowledgeCenter.category')}
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select a category</option>
                <option value="General">General</option>
                <option value="Products">Products</option>
                <option value="Services">Services</option>
                <option value="Solutions">Solutions</option>
                <option value="Events">Events</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('knowledgeCenter.sourceType')}
              </label>
              <select
                value={formData.sourceType}
                onChange={(e) => setFormData({...formData, sourceType: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="Manual">Manual</option>
                <option value="Excel">Excel</option>
                <option value="PDF">PDF</option>
                <option value="AI">AI Generated</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('knowledgeCenter.tags')}
            </label>
            <TagService
              entityType="Knowledge"
              entityId={article?.ArticleID}
              initialTags={formData.tags ? (typeof formData.tags === 'string' ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : formData.tags) : []}
              maxTags={15}
              onChange={(newTags) => {
                setFormData({...formData, tags: newTags.join(', ')});
              }}
              placeholder={t('knowledgeCenter.tagsPlaceholder', 'Add tags...')}
            />
          </div>

          {/* File Upload Section */}
          <div className="space-y-4">
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Upload className="inline h-4 w-4 mr-2" />
                {t('knowledgeCenter.attachFiles', 'Attach Files')}
              </label>
              <div className="space-y-3">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('knowledgeCenter.supportedFiles', 'Supported files: PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX, JPG, PNG, GIF (max 50MB each)')}
                </p>
                
                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('knowledgeCenter.selectedFiles', 'Selected Files')}:
                    </h4>
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                        <div className="flex items-center space-x-2">
                          {getFileIcon(file.type)}
                          <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">({formatFileSize(file.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSelectedFile(index)}
                          className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Existing Files */}
            {article && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FileText className="inline h-4 w-4 mr-2" />
                  {t('knowledgeCenter.existingFiles', 'Existing Files')}
                </h4>
                {loadingFiles ? (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('knowledgeCenter.loadingFiles', 'Loading files...')}
                  </div>
                ) : files.length > 0 ? (
                  <div className="space-y-2">
                    {files.map((file) => (
                      <div key={file.FileID} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 p-2 rounded">
                        <div className="flex items-center space-x-2">
                          {getFileIcon(file.MimeType)}
                          <span className="text-sm text-gray-700 dark:text-gray-300">{file.FileName}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">({formatFileSize(file.FileSize)})</span>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            type="button"
                            onClick={() => downloadFile(file.FileID, file.FileName)}
                            className="text-blue-500 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 p-1"
                            title={t('common.download', 'Download')}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteFile(file.FileID)}
                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                            title={t('common.delete', 'Delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('knowledgeCenter.noFilesAttached', 'No files attached')}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving || uploadingFiles}
              className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50"
            >
              {saving || uploadingFiles ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Article View Modal Component for displaying full article content
const ArticleViewModal = ({ 
  article, 
  onClose, 
  onEdit, 
  onDelete, 
  loading, 
  canModify, 
  isManager,
  user,
  onRefresh,
  // Attachment viewer props
  handleViewAttachment,
  handleDownloadAttachment,
  handleCloseViewAttachment,
  handleMouseDown,
  decodeFileName,
  formatFileSize,
  viewingAttachment,
  showViewAttachmentModal,
  selectedAttachmentForView,
  attachmentViewUrl,
  modalSize,
  setModalSize,
  buildApiUrl
}) => {
  const { t } = useTranslation();

  const getSourceTypeIcon = (sourceType) => {
    switch (sourceType) {
      case 'Manual': return <Edit className="h-4 w-4" />;
      case 'Excel': return <FileText className="h-4 w-4" />;
      case 'PDF': return <FileText className="h-4 w-4" />;
      case 'AI': return <Brain className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getSourceTypeColor = (sourceType) => {
    switch (sourceType) {
      case 'Manual': return 'bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200';
      case 'Excel': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'PDF': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'AI': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{article.Title}</h2>
              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-1">
                  <User className="h-4 w-4" />
                  <span>{article.CreatedByName}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(article.UpdatedAt).toLocaleDateString()}</span>
                </div>
                {article.Category && (
                  <span className="bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 text-xs px-2 py-1 rounded-full">
                    {article.Category}
                  </span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${getSourceTypeColor(article.SourceType)}`}>
                  {getSourceTypeIcon(article.SourceType)}
                  {article.SourceType}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              {canModify && (
                <>
                  <button
                    onClick={() => {
                      onClose(); // Close the view modal first
                      onEdit(article); // This will open the edit modal
                    }}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900"
                    title={isManager ? "Edit article (Manager)" : "Edit your article"}
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t('knowledgeCenter.deleteConfirm'))) {
                        onDelete(article.ArticleID);
                        onClose();
                      }
                    }}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-lg hover:bg-red-50 dark:hover:bg-red-900"
                    title={isManager ? "Delete article (Manager)" : "Delete your article"}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  
                  {/* PDF Processing for RAG Button */}
                  {article.files && article.files.some(file => file.MimeType === 'application/pdf') && (
                    <button
                      onClick={() => {
                        // We need to pass the handleProcessPDFsForRAG function to this modal
                        // For now, we'll show a message to use the main view
                        toast('Use the "Process PDFs" button in the main article view to process PDFs for RAG ingestion.');
                      }}
                      className="p-2 text-green-400 dark:text-green-500 hover:text-green-600 dark:hover:text-green-400 transition-colors rounded-lg hover:bg-green-50 dark:hover:bg-green-900"
                      title="Process PDFs for RAG ingestion"
                    >
                      <Brain className="h-5 w-5" />
                    </button>
                  )}
                </>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 dark:border-teal-400"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-300">{t('common.loading')}</span>
            </div>
          ) : (
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 dark:text-gray-300 leading-relaxed">
                {article.Content || article.ContentPreview}
              </div>
            </div>
          )}

          {!loading && (
            <>
              {article.Tags && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('knowledgeCenter.tags')}</h4>
                  <TagService
                    entityType="Knowledge"
                    entityId={article.ArticleID}
                    initialTags={article.Tags ? (typeof article.Tags === 'string' ? article.Tags.split(',').map(t => t.trim()).filter(Boolean) : article.Tags) : []}
                    readonly={!user?.roles?.some(role => ['Admin', 'Manager'].includes(role.RoleName))}
                    maxTags={15}
                    onChange={async (newTags) => {
                      // Refresh article after tag change
                      if (onRefresh) {
                        await onRefresh();
                      }
                    }}
                  />
                </div>
              )}

              {article.files && article.files.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <FileText className="inline h-4 w-4 mr-2" />
                    {t('knowledgeCenter.attachedFiles', 'Attached Files')} ({article.files.length})
                  </h4>
                  <div className="space-y-2">
                    {article.files.map((file) => (
                      <div key={file.FileID} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileIcon className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => handleViewAttachment(file)}
                              disabled={viewingAttachment}
                              className="text-sm font-medium text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer text-left truncate block"
                              title={`${t('common.view') || 'View'} ${decodeFileName(file.FileName)}`}
                            >
                              {decodeFileName(file.FileName)}
                            </button>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(file.FileSize)} • {file.MimeType}
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleViewAttachment(file)}
                            disabled={viewingAttachment}
                            className={`p-1 transition-colors ${
                              viewingAttachment 
                                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                                : 'text-gray-400 hover:text-green-600 dark:hover:text-green-400'
                            }`}
                            title={viewingAttachment 
                              ? (t('common.loading') || 'Loading attachment...') 
                              : `${t('common.view') || 'View'} ${decodeFileName(file.FileName)}`
                            }
                          >
                            {viewingAttachment ? (
                              <div className="w-4 h-4 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDownloadAttachment(file)}
                            className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                            title={`${t('common.download') || 'Download'} ${decodeFileName(file.FileName)}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Enhanced View Attachment Modal */}
      {showViewAttachmentModal && selectedAttachmentForView && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={handleCloseViewAttachment}
        >
          <div 
            className="relative modal-container bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: modalSize.width, 
              height: modalSize.height,
              minWidth: '600px', 
              minHeight: '400px',
              maxWidth: '95vw',
              maxHeight: '95vh'
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Paperclip className="w-5 h-5 text-gray-500" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {decodeFileName(selectedAttachmentForView.FileName)}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatFileSize(selectedAttachmentForView.FileSize)} • {selectedAttachmentForView.MimeType}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDownloadAttachment(selectedAttachmentForView)}
                  className="p-2 text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                  title={t('attachments.downloadAttachment', 'Download Attachment')}
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setModalSize({ width: '98vw', height: '92vh' })}
                  className="p-2 text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                  title={t('attachments.maximize', 'Maximize')}
                >
                  <Maximize2 className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCloseViewAttachment}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title={t('common.close') || 'Close'}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4 relative">
              {/* Resize Handle */}
              <div
                className="absolute bottom-2 right-2 w-6 h-6 cursor-se-resize opacity-50 hover:opacity-100 transition-opacity"
                onMouseDown={handleMouseDown}
                style={{
                  background: 'linear-gradient(135deg, transparent 50%, #9ca3af 50%)',
                  borderRadius: '0 0 4px 0'
                }}
                title={t('attachments.resize', 'Resize')}
              />
              {viewingAttachment ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500 dark:text-gray-400">
                    {t('common.loading') || 'Loading attachment...'}
                  </div>
                </div>
              ) : attachmentViewUrl ? (
                <div className="w-full h-full">
                  {/* Image Preview */}
                  {selectedAttachmentForView.MimeType && selectedAttachmentForView.MimeType.startsWith('image/') ? (
                    <div className="flex items-center justify-center w-full h-full">
                                             <img
                         src={buildApiUrl(`/api/knowledge/files/${selectedAttachmentForView.FileID}/view?t=${Date.now()}`)}
                         alt={decodeFileName(selectedAttachmentForView.FileName)}
                         className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                         style={{ maxHeight: 'calc(100vh - 200px)' }}
                         onLoad={(e) => {
                           console.log('🔍 Image loaded successfully:', {
                             attachmentId: selectedAttachmentForView.FileID,
                             fileName: selectedAttachmentForView.FileName,
                             naturalWidth: e.target.naturalWidth,
                             naturalHeight: e.target.naturalHeight
                           });
                         }}
                         onError={(e) => {
                           console.error('🔍 Direct URL failed, trying blob URL');
                           e.target.src = attachmentViewUrl;
                           e.target.onerror = () => {
                             console.error('🔍 Both direct URL and blob URL failed');
                             toast.error(t('knowledgeCenter.failedToLoadImage') || 'Failed to load image');
                           };
                         }}
                       />
                    </div>
                  ) : selectedAttachmentForView.MimeType && selectedAttachmentForView.MimeType === 'application/pdf' ? (
                    /* PDF Preview */
                    <div className="w-full h-full">
                      <iframe
                        src={attachmentViewUrl}
                        className="w-full h-full border-0 rounded-lg shadow-lg"
                        style={{ minHeight: 'calc(100vh - 200px)' }}
                        title={decodeFileName(selectedAttachmentForView.FileName)}
                        onLoad={() => console.log('🔍 PDF loaded successfully:', selectedAttachmentForView.FileID)}
                        onError={(e) => {
                          console.error('🔍 Failed to load PDF:', selectedAttachmentForView.FileID, e);
                          toast.error(t('knowledgeCenter.failedToLoadPDF') || 'Failed to load PDF');
                        }}
                      />
                    </div>
                  ) : selectedAttachmentForView.MimeType && (selectedAttachmentForView.MimeType.startsWith('text/') || selectedAttachmentForView.MimeType === 'application/json' || selectedAttachmentForView.MimeType === 'application/xml') ? (
                    /* Text File Preview */
                    <div className="w-full h-full">
                      <iframe
                        src={attachmentViewUrl}
                        className="w-full h-full border-0 rounded-lg shadow-lg"
                        style={{ minHeight: 'calc(100vh - 200px)' }}
                        title={decodeFileName(selectedAttachmentForView.FileName)}
                        onLoad={() => console.log('🔍 Text file loaded successfully:', selectedAttachmentForView.FileID)}
                        onError={(e) => {
                          console.error('🔍 Failed to load text file:', selectedAttachmentForView.FileID, e);
                          toast.error(t('knowledgeCenter.failedToLoadTextFile') || 'Failed to load text file');
                        }}
                      />
                    </div>
                  ) : selectedAttachmentForView.MimeType && (selectedAttachmentForView.MimeType.startsWith('audio/') || selectedAttachmentForView.MimeType.startsWith('video/')) ? (
                    /* Audio/Video Preview */
                    <div className="flex items-center justify-center h-full">
                      {selectedAttachmentForView.MimeType.startsWith('audio/') ? (
                        <audio controls className="max-w-full">
                          <source src={attachmentViewUrl} type={selectedAttachmentForView.MimeType} />
                          {t('knowledgeCenter.audioNotSupported') || 'Your browser does not support audio playback.'}
                        </audio>
                      ) : (
                        <video controls className="max-w-full max-h-full">
                          <source src={attachmentViewUrl} type={selectedAttachmentForView.MimeType} />
                          {t('knowledgeCenter.videoNotSupported') || 'Your browser does not support video playback.'}
                        </video>
                      )}
                    </div>
                  ) : (
                    /* Default Preview - Show file info and download button */
                    <div className="flex flex-col items-center justify-center h-64 space-y-4">
                      <div className="text-center">
                        <Paperclip className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {decodeFileName(selectedAttachmentForView.FileName)}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          {formatFileSize(selectedAttachmentForView.FileSize)} • {selectedAttachmentForView.MimeType}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('attachments.previewNotAvailable', 'Preview not available for this file type. Please download to view.')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDownloadAttachment(selectedAttachmentForView)}
                        className="inline-flex items-center px-4 py-2 bg-teal-600 text-white font-medium rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t('attachments.downloadToView', 'Download to View')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-red-500 dark:text-red-400">
                    {t('attachments.failedToLoadAttachment', 'Failed to load attachment')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeCenter;